import React from 'react';
import { modDatabase } from 'data/mods';
import { UNIQUE_IDOLS } from './constants/uniqueIdols';
import ValueModifierInput from './ValueModifierInput';
import { 
  TwistedWishFormProps, 
  FixedModifier,
  FixedValueModifier,
  VariableModifier
} from './types';

const TwistedWishForm: React.FC<TwistedWishFormProps> = ({
  idolModifiers,
  selectedVariableModifiers,
  updateModifierValue,
  replaceVariableModifier,
  isVariableModifierSelected
}) => {
  // Find the Twisted Wish idol definition
  const twistedWishIdol = UNIQUE_IDOLS.find(idol => idol.id === 'twisted-wish');
  
  if (!twistedWishIdol) {
    return <div className="text-red-500">Twisted Wish configuration not found</div>;
  }

  // Fixed modifiers (always present)
  const fixedModifiers = idolModifiers.filter(mod => mod.isFixed) as (FixedModifier | FixedValueModifier)[];
  
  // Get available variable modifiers for selection
  const getAvailableModifiers = (index: number) => {
    if (!twistedWishIdol.variableModifiers) return [];
    
    // Always include the currently selected modifier at this index
    const currentModId = selectedVariableModifiers[index];
    
    // In edit mode, make sure we keep showing previously selected modifiers
    if (currentModId) {
      return twistedWishIdol.variableModifiers.filter(mod => {
        // Allow if it's the current selection at this index
        if (mod.modId === currentModId) return true;
        
        // Otherwise, only show if not already selected elsewhere
        return !isVariableModifierSelected(mod.modId);
      });
    }
    
    // For empty slots, show all available modifiers not already selected
    return twistedWishIdol.variableModifiers.filter(mod => {
      // Allow if it's the current selection at this index
      if (mod.modId === currentModId) return true;
      
      // Otherwise, only show if not already selected elsewhere
      return !isVariableModifierSelected(mod.modId);
    });
  };

  // Number of variable modifier slots
  const variableSlotCount = twistedWishIdol.variableModifiersRequired || 3;
  
  // Find or create a modifier object based on a modId
  const getModifierForInput = (modId: number): VariableModifier => {
    // First, try to find the modifier in idolModifiers
    const existingMod = idolModifiers.find(mod => mod.modId === modId);
    if (existingMod && existingMod.isAdjustable) {
      return existingMod as VariableModifier;
    }
    
    // If not found in idolModifiers, find the definition in variableModifiers
    const modDefinition = twistedWishIdol.variableModifiers?.find(mod => mod.modId === modId);
    
    // Create and return a new VariableModifier object
    return {
      modId: modId,
      defaultValue: modDefinition?.defaultValue || 85,
      range: modDefinition?.range || [80, 95],
      isFixed: false,
      isAdjustable: true,
      value: modDefinition?.defaultValue || 85
    };
  };

  return (
    <div className="mb-4">
      {/* Display fixed modifiers */}
      {fixedModifiers.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm text-white mb-2">Fixed Modifiers:</h4>
          <div className="space-y-2">
            {fixedModifiers.map(mod => (
              <div key={mod.modId} className="bg-gray-700 p-2 rounded">
                <span className="text-white">
                  {modDatabase.mods[mod.modId]?.description || `Modifier #${mod.modId}`}
                  {mod.defaultValue ? `: ${mod.defaultValue}` : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Variable modifier selection */}
      <h4 className="text-sm text-white mb-2">Select {variableSlotCount} variable modifiers:</h4>
      {Array.from({ length: variableSlotCount }).map((_, index) => {
        const selectedModId = selectedVariableModifiers[index];
        const availableModifiers = getAvailableModifiers(index);
        
        return (
          <div key={index} className="mb-3 bg-gray-700 p-3 rounded">
            <label className="text-sm text-white block mb-2">Variable Modifier {index + 1}</label>
            <select 
              value={selectedModId || ''}
              onChange={(e) => replaceVariableModifier(index, Number(e.target.value))}
              className="w-full p-2 rounded bg-gray-800 text-white"
            >
              <option value="">Select Modifier</option>
              {availableModifiers.map(mod => (
                <option 
                  key={mod.modId} 
                  value={mod.modId}
                  disabled={isVariableModifierSelected(mod.modId) && selectedModId !== mod.modId}
                >
                  {modDatabase.mods[mod.modId]?.description || `Mod #${mod.modId}`}
                </option>
              ))}
            </select>
            
            {/* Always display value input if a modifier is selected */}
            {selectedModId && (
              <div className="mt-3">
                <ValueModifierInput
                  key={`input-${selectedModId}`}
                  modifier={getModifierForInput(selectedModId)}
                  onChange={(value) => updateModifierValue(selectedModId, value)}
                />
              </div>
            )}
          </div>
        );
      })}
      
      {/* Show validation status */}
      {selectedVariableModifiers.filter(Boolean).length < variableSlotCount && (
        <div className="text-yellow-500 text-sm mt-2">
          Please select {variableSlotCount - selectedVariableModifiers.filter(Boolean).length} more modifier
          {variableSlotCount - selectedVariableModifiers.filter(Boolean).length === 1 ? '' : 's'}.
        </div>
      )}
    </div>
  );
};

export default TwistedWishForm;